/**
 * F60b — poll a loader only while the tab is visible. F61 — moved from
 * apps/admin to ui-web so the portal shares it (web only: it uses `document`).
 *
 *   - Runs once on mount (if visible), then again `intervalMs` after each run
 *     finishes — a `setTimeout` chain, never overlapping runs.
 *   - Hidden tab: the timer stops, so a background tab sends nothing.
 *   - Visible again: runs at once (unless it ran < 2 s ago), then resumes.
 *   - Optional idle slow-down: after `idleAfterMs` with no pointer, key, wheel
 *     or window-focus input the interval becomes `idleIntervalMs`; the next
 *     input runs at once and restores `intervalMs`.
 *   - `refreshNow()` runs immediately (trigger `'request'`); a call that lands
 *     during a run is queued and runs right after it.
 *
 * The loader owns its errors; a rejected run just schedules the next one.
 */
import { useCallback, useEffect, useRef } from 'react';

export type PollTrigger = 'start' | 'interval' | 'visible' | 'active' | 'request';

export interface PollWhileVisibleOptions {
  readonly intervalMs: number;
  /** Slow down after this long without input (default: never). */
  readonly idleAfterMs?: number;
  /** The interval while idle (default: `intervalMs`). */
  readonly idleIntervalMs?: number;
  /** `false` stops polling entirely (default `true`). */
  readonly enabled?: boolean;
}

/** A tab that just came back within this window isn't re-polled. */
const VISIBLE_DEBOUNCE_MS = 2_000;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'focus'] as const;

export function usePollWhileVisible(
  load: (trigger: PollTrigger) => Promise<unknown> | void,
  { intervalMs, idleAfterMs, idleIntervalMs, enabled = true }: PollWhileVisibleOptions,
): { readonly refreshNow: () => void } {
  const loadRef = useRef(load);
  const runRef = useRef<((trigger: PollTrigger) => void) | null>(null);

  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    if (!enabled) return undefined;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;
    let queued: PollTrigger | null = null;
    let disposed = false;
    let lastRun = 0;
    let lastActivity = Date.now();

    const isVisible = () => document.visibilityState !== 'hidden';
    const isIdle = () => idleAfterMs !== undefined && Date.now() - lastActivity >= idleAfterMs;
    const clear = () => {
      if (timer !== null) clearTimeout(timer);
      timer = null;
    };
    const schedule = () => {
      clear();
      if (disposed || !isVisible()) return;
      const delay = isIdle() ? (idleIntervalMs ?? intervalMs) : intervalMs;
      timer = setTimeout(() => void run('interval'), delay);
    };
    const run = async (trigger: PollTrigger): Promise<void> => {
      if (disposed) return;
      if (inFlight) {
        if (queued === null || trigger === 'request') queued = trigger;
        return;
      }
      clear();
      inFlight = true;
      lastRun = Date.now();
      try {
        await loadRef.current(trigger);
      } catch {
        // The loader reports its own errors; keep polling.
      } finally {
        inFlight = false;
      }
      if (queued !== null) {
        const next = queued;
        queued = null;
        void run(next);
        return;
      }
      schedule();
    };

    const onVisibility = () => {
      if (!isVisible()) {
        clear();
        return;
      }
      if (Date.now() - lastRun < VISIBLE_DEBOUNCE_MS) schedule();
      else void run('visible');
    };
    const onActivity = () => {
      const wasIdle = isIdle();
      lastActivity = Date.now();
      if (wasIdle && isVisible()) void run('active');
    };

    runRef.current = (trigger) => void run(trigger);
    document.addEventListener('visibilitychange', onVisibility);
    if (idleAfterMs !== undefined) {
      for (const type of ACTIVITY_EVENTS) {
        window.addEventListener(type, onActivity, { passive: true });
      }
    }
    if (isVisible()) void run('start');

    return () => {
      disposed = true;
      clear();
      runRef.current = null;
      document.removeEventListener('visibilitychange', onVisibility);
      for (const type of ACTIVITY_EVENTS) window.removeEventListener(type, onActivity);
    };
  }, [enabled, intervalMs, idleAfterMs, idleIntervalMs]);

  const refreshNow = useCallback(() => runRef.current?.('request'), []);
  return { refreshNow };
}
